import { ALLERGENS, DIETARY_LABELS, MEAL_TYPES, type CategoryTreeNode } from '@recipes/contracts';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Checkbox,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Input,
  Label,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  StarRating,
} from '@recipes/ui';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChipsInput } from '@/components/forms/chips-input';
import { api } from '@/lib/api';
import { currentLocale } from '@/lib/i18n';
import { queryKeys } from '@/lib/query-keys';
import { DEFAULT_FILTERS, type RecipeFilterState } from './filter-state';
import { useCategories, useTags } from './queries';

export interface RecipeFilterSheetProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  value: RecipeFilterState;
  onApply: (next: RecipeFilterState) => void;
}

const ANY = '__any__';

function CategoryTree({
  nodes,
  selected,
  onToggle,
  depth = 0,
}: {
  nodes: CategoryTreeNode[];
  selected: string[];
  onToggle: (id: string) => void;
  depth?: number;
}) {
  const locale = currentLocale();
  return (
    <ul className={depth > 0 ? 'ml-5 border-l pl-2' : ''}>
      {nodes.map((node) => (
        <li key={node.id}>
          <div className="flex min-h-[36px] items-center gap-2">
            <Checkbox
              id={`cat-${node.id}`}
              checked={selected.includes(node.id)}
              onCheckedChange={() => onToggle(node.id)}
            />
            <Label htmlFor={`cat-${node.id}`} className="font-normal">
              {node.name[locale]}
              {node.recipeCount !== undefined && (
                <span className="ml-1 text-xs text-muted-foreground">({node.recipeCount})</span>
              )}
            </Label>
          </div>
          {node.children.length > 0 && (
            <CategoryTree
              nodes={node.children}
              selected={selected}
              onToggle={onToggle}
              depth={depth + 1}
            />
          )}
        </li>
      ))}
    </ul>
  );
}

/** Full filter form (spec 5.8) rendered as a sheet; changes apply on submit. */
export function RecipeFilterSheet({ open, onOpenChange, value, onApply }: RecipeFilterSheetProps) {
  const { t } = useTranslation();
  const [draft, setDraft] = useState<RecipeFilterState>(value);
  useEffect(() => {
    if (open) setDraft(value);
  }, [open, value]);

  const categories = useCategories();
  const tags = useTags();
  const facets = useQuery({
    queryKey: queryKeys.search.facets({}),
    queryFn: () => api.search.facets(),
    enabled: open,
    staleTime: 5 * 60_000,
    retry: false,
  });
  const cuisines = facets.data?.cuisines.map((c) => c.value) ?? [];
  const mealTypes = Array.from(
    new Set([...MEAL_TYPES, ...(facets.data?.mealTypes.map((m) => m.value) ?? [])]),
  );

  const patch = (p: Partial<RecipeFilterState>) => setDraft((d) => ({ ...d, ...p }));
  const toggleIn = (key: 'categoryIds' | 'dietary' | 'excludeAllergens', id: string) =>
    setDraft((d) => ({
      ...d,
      [key]: d[key].includes(id) ? d[key].filter((x) => x !== id) : [...d[key], id],
    }));

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        fullscreenOnMobile
        closeLabel={t('common.close')}
        className="sm:max-w-xl"
        data-testid="filter-sheet"
      >
        <DialogHeader>
          <DialogTitle>{t('recipes.filters.title')}</DialogTitle>
          <DialogDescription>{t('recipes.filters.description')}</DialogDescription>
        </DialogHeader>
        <form
          className="space-y-5"
          onSubmit={(e) => {
            e.preventDefault();
            onApply(draft);
            onOpenChange(false);
          }}
        >
          <div className="flex items-center gap-2">
            <Checkbox
              id="f-favorite"
              checked={draft.favorite}
              onCheckedChange={(v) => patch({ favorite: v === true })}
            />
            <Label htmlFor="f-favorite">{t('recipes.filters.favoritesOnly')}</Label>
          </div>
          <div className="flex items-center gap-2">
            <Checkbox
              id="f-base"
              checked={draft.baseRecipe}
              onCheckedChange={(v) => patch({ baseRecipe: v === true })}
              data-testid="filter-base-recipes"
            />
            <Label htmlFor="f-base">{t('baseRecipes.filter')}</Label>
          </div>

          <fieldset>
            <legend className="mb-1 text-sm font-medium">{t('recipes.filters.categories')}</legend>
            {categories.data && categories.data.length > 0 ? (
              <div className="max-h-48 overflow-y-auto rounded-md border p-2">
                <CategoryTree
                  nodes={categories.data}
                  selected={draft.categoryIds}
                  onToggle={(id) => toggleIn('categoryIds', id)}
                />
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">{t('recipes.filters.noCategories')}</p>
            )}
          </fieldset>

          <div>
            <Label className="mb-1 block">{t('recipes.filters.tags')}</Label>
            <ChipsInput
              value={draft.tags}
              onChange={(tags) => patch({ tags })}
              suggestions={tags.data?.map((x) => x.name) ?? []}
              label={t('recipes.filters.tags')}
              removeLabel={t('common.delete')}
              placeholder={t('recipes.filters.tagsPlaceholder')}
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label htmlFor="f-cuisine" className="mb-1 block">
                {t('recipes.filters.cuisine')}
              </Label>
              <Input
                id="f-cuisine"
                list="f-cuisine-list"
                value={draft.cuisine}
                onChange={(e) => patch({ cuisine: e.target.value })}
              />
              <datalist id="f-cuisine-list">
                {cuisines.map((c) => (
                  <option key={c} value={c} />
                ))}
              </datalist>
            </div>
            <div>
              <Label htmlFor="f-mealtype" className="mb-1 block">
                {t('recipes.filters.mealType')}
              </Label>
              <Select
                value={draft.mealType || ANY}
                onValueChange={(v) => patch({ mealType: v === ANY ? '' : v })}
              >
                <SelectTrigger id="f-mealtype">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>{t('recipes.filters.any')}</SelectItem>
                  {mealTypes.map((m) => (
                    <SelectItem key={m} value={m}>
                      {t(`recipes.mealType.${m}`, { defaultValue: m })}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="f-difficulty" className="mb-1 block">
                {t('recipes.filters.difficulty')}
              </Label>
              <Select
                value={draft.difficulty || ANY}
                onValueChange={(v) =>
                  patch({ difficulty: v === ANY ? '' : (v as RecipeFilterState['difficulty']) })
                }
              >
                <SelectTrigger id="f-difficulty">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={ANY}>{t('recipes.filters.any')}</SelectItem>
                  {(['EASY', 'MEDIUM', 'HARD'] as const).map((d) => (
                    <SelectItem key={d} value={d}>
                      {t(`recipes.difficulty.${d}`)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="f-maxtime" className="mb-1 block">
                {t('recipes.filters.maxTime')}
              </Label>
              <Input
                id="f-maxtime"
                type="number"
                inputMode="numeric"
                min={1}
                step={5}
                value={draft.maxTotalMinutes ?? ''}
                onChange={(e) =>
                  patch({ maxTotalMinutes: e.target.value ? Number(e.target.value) : null })
                }
              />
            </div>
          </div>

          <div>
            <Label className="mb-1 block">{t('recipes.filters.minRating')}</Label>
            <div className="flex items-center gap-3">
              <StarRating
                value={draft.minRating}
                onChange={(v) => patch({ minRating: v })}
                allowHalf={false}
                label={t('recipes.filters.minRating')}
              />
              {draft.minRating && (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  onClick={() => patch({ minRating: null })}
                >
                  {t('recipes.filters.clear')}
                </Button>
              )}
            </div>
          </div>

          <fieldset>
            <legend className="mb-1 text-sm font-medium">{t('recipes.filters.dietary')}</legend>
            <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
              {DIETARY_LABELS.map((label) => (
                <div key={label} className="flex min-h-[36px] items-center gap-2">
                  <Checkbox
                    id={`f-diet-${label}`}
                    checked={draft.dietary.includes(label)}
                    onCheckedChange={() => toggleIn('dietary', label)}
                  />
                  <Label htmlFor={`f-diet-${label}`} className="font-normal">
                    {t(`recipes.labels.dietary.${label}`)}
                  </Label>
                </div>
              ))}
            </div>
          </fieldset>

          <fieldset>
            <legend className="mb-1 text-sm font-medium">
              {t('recipes.filters.excludeAllergens')}
            </legend>
            <div className="grid grid-cols-2 gap-1 sm:grid-cols-3">
              {ALLERGENS.map((a) => (
                <div key={a} className="flex min-h-[36px] items-center gap-2">
                  <Checkbox
                    id={`f-all-${a}`}
                    checked={draft.excludeAllergens.includes(a)}
                    onCheckedChange={() => toggleIn('excludeAllergens', a)}
                  />
                  <Label htmlFor={`f-all-${a}`} className="font-normal">
                    {t(`recipes.labels.allergen.${a}`)}
                  </Label>
                </div>
              ))}
            </div>
          </fieldset>

          <div>
            <Label className="mb-1 block">{t('recipes.filters.availableIngredients')}</Label>
            <ChipsInput
              value={draft.availableIngredients}
              onChange={(availableIngredients) => patch({ availableIngredients })}
              label={t('recipes.filters.availableIngredients')}
              removeLabel={t('common.delete')}
              placeholder={t('recipes.filters.availablePlaceholder')}
              data-testid="filter-available-ingredients"
            />
            <p className="mt-1 text-xs text-muted-foreground">
              {t('recipes.filters.availableHint')}
            </p>
          </div>

          <div className="flex items-center gap-2">
            <Checkbox
              id="f-archived"
              checked={draft.includeArchived}
              onCheckedChange={(v) => patch({ includeArchived: v === true })}
            />
            <Label htmlFor="f-archived">{t('recipes.filters.includeArchived')}</Label>
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="ghost"
              onClick={() =>
                setDraft({
                  ...DEFAULT_FILTERS,
                  q: draft.q,
                  sort: draft.sort,
                  trashed: draft.trashed,
                })
              }
            >
              {t('recipes.filters.reset')}
            </Button>
            <Button type="submit" data-testid="filter-apply">
              {t('recipes.filters.apply')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
